import React, { useEffect, useState } from 'react';
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function buildUrl(path) {
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(buildUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new Error(data?.detail || `Request failed (${response.status})`);
  return data;
}

const PROVIDERS = [
  {
    key: 'titlesearcher',
    label: 'TitleSearcher',
    description: 'Used for Tennessee property searches in Hawkins and Greene Counties.',
  },
  {
    key: 'ustitlesearch',
    label: 'US Title Search',
    description: 'Used for all other counties in the case search plan.',
  },
];

const emptyProvider = { username: '', password: '', enabled: false };

export default function TitleSearchSettings() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    titlesearcher: { ...emptyProvider },
    ustitlesearch: { ...emptyProvider },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [batchPreflight, setBatchPreflight] = useState(null);
  const [batch, setBatch] = useState(null);
  const [batchLoading, setBatchLoading] = useState(true);
  const [batchStarting, setBatchStarting] = useState(false);
  const [batchError, setBatchError] = useState('');

  const applySettings = (data) => {
    setSettings(data);
    setForm((current) => {
      const next = { ...current };
      PROVIDERS.forEach(({ key }) => {
        next[key] = {
          username: '',
          password: '',
          enabled: Boolean(data?.providers?.[key]?.enabled),
        };
      });
      return next;
    });
  };

  useEffect(() => {
    let active = true;
    apiFetch('/firm/title-search-credentials/')
      .then((data) => { if (active) applySettings(data); })
      .catch((requestError) => {
        if (active) setError(requestError.message || 'Could not load title-search settings.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const loadBatchPreflight = async ({ quiet = false } = {}) => {
    if (!quiet) setBatchLoading(true);
    try {
      const data = await apiFetch('/firm/title-search-batches/preflight/');
      setBatchPreflight(data);
      setBatch((current) => current || data.latest_batch || null);
      setBatchError('');
      return data;
    } catch (requestError) {
      setBatchError(requestError.message || 'Could not load the Chapter 7 bulk-search summary.');
      return null;
    } finally {
      if (!quiet) setBatchLoading(false);
    }
  };

  useEffect(() => {
    loadBatchPreflight();
    // This summary is refreshed again after a running batch finishes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!batch?.id || !['queued', 'in_progress'].includes(batch.status)) return undefined;
    let active = true;
    const timer = window.setInterval(async () => {
      try {
        const nextBatch = await apiFetch(`/firm/title-search-batches/${batch.id}/`);
        if (!active) return;
        setBatch(nextBatch);
        if (!['queued', 'in_progress'].includes(nextBatch.status)) {
          window.clearInterval(timer);
          await loadBatchPreflight({ quiet: true });
        }
      } catch (requestError) {
        if (active) setBatchError(requestError.message || 'Could not refresh the bulk-search status.');
      }
    }, 3000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch?.id, batch?.status]);

  const updateProvider = (key, field, value) => {
    setForm((current) => ({
      ...current,
      [key]: { ...current[key], [field]: value },
    }));
  };

  const save = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    const providers = {};
    PROVIDERS.forEach(({ key }) => {
      providers[key] = { enabled: form[key].enabled };
      if (form[key].username.trim()) providers[key].username = form[key].username.trim();
      if (form[key].password) providers[key].password = form[key].password;
    });
    try {
      const data = await apiFetch('/firm/title-search-credentials/', {
        method: 'PUT',
        body: JSON.stringify({ providers }),
      });
      applySettings(data);
      setMessage('Title-search settings saved.');
      await loadBatchPreflight({ quiet: true });
    } catch (requestError) {
      setError(requestError.message || 'Could not save title-search settings.');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm('Remove both saved title-search usernames and passwords?')) return;
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await apiFetch('/firm/title-search-credentials/', { method: 'DELETE' });
      applySettings({
        encryption_ready: settings?.encryption_ready,
        providers: {
          titlesearcher: { enabled: false, configured: false, ready: false },
          ustitlesearch: { enabled: false, configured: false, ready: false },
        },
      });
      setMessage('Saved title-search credentials removed.');
      await loadBatchPreflight({ quiet: true });
    } catch (requestError) {
      setError(requestError.message || 'Could not remove title-search credentials.');
    } finally {
      setSaving(false);
    }
  };

  const startBatch = async () => {
    const caseCount = batchPreflight?.ready_case_count || 0;
    const searchCount = batchPreflight?.total_searches || 0;
    const blockedCount = batchPreflight?.blocked_case_count || 0;
    const confirmed = window.confirm(
      `Run ${searchCount} live title search${searchCount === 1 ? '' : 'es'} for ${caseCount} filed Chapter 7 case${caseCount === 1 ? '' : 's'}? `
      + `${blockedCount ? `${blockedCount} case${blockedCount === 1 ? '' : 's'} with missing information will be identified but skipped. ` : ''}`
      + 'This can use paid searches or search units on the TitleSearcher and US Title Search accounts. Existing results will not be reused.'
    );
    if (!confirmed) return;
    setBatchStarting(true);
    setBatchError('');
    try {
      const nextBatch = await apiFetch('/firm/title-search-batches/start/', {
        method: 'POST',
        body: JSON.stringify({ confirmed: true }),
      });
      setBatch(nextBatch);
    } catch (requestError) {
      setBatchError(requestError.message || 'Could not start the filed Chapter 7 title searches.');
    } finally {
      setBatchStarting(false);
    }
  };

  const downloadBatchPDF = async () => {
    if (!batch?.document?.download_url) return;
    setBatchError('');
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(buildUrl(batch.document.download_url), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(`Could not download the PDF (${response.status}).`);
      const blobUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = batch.document.filename || 'Filed_Chapter_7_Title_Search_Results.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (requestError) {
      setBatchError(requestError.message || 'Could not download the combined title-search PDF.');
    }
  };

  if (loading) {
    return <section className="rounded-lg bg-white p-6 shadow">Loading title-search settings…</section>;
  }

  const anyConfigured = PROVIDERS.some(({ key }) => settings?.providers?.[key]?.configured);

  return (
    <section id="title-search-accounts" className="scroll-mt-6 rounded-lg bg-white p-6 shadow">
      <div className="flex items-start gap-3">
        <LockClosedIcon className="mt-0.5 h-6 w-6 shrink-0 text-indigo-700" />
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Title Search Accounts</h2>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-600">
            Save the two land-record site logins used by the case-home title search. Usernames and
            passwords are encrypted and are never returned to the browser after saving.
          </p>
        </div>
      </div>

      {!settings?.encryption_ready && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          The server credential-encryption key is not configured, so these logins cannot be saved yet.
        </div>
      )}
      {message && <div className="mt-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">{message}</div>}
      {error && <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>}

      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
        {PROVIDERS.map(({ key, label, description }) => {
          const saved = settings?.providers?.[key] || {};
          return (
            <div key={key} className="rounded-lg border border-gray-200 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{label}</h3>
                  <p className="mt-1 text-xs leading-5 text-gray-600">{description}</p>
                </div>
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                  saved.ready ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                }`}>
                  {saved.ready ? <CheckCircleIcon className="h-4 w-4" /> : <ExclamationTriangleIcon className="h-4 w-4" />}
                  {saved.ready ? 'Ready' : 'Setup required'}
                </span>
              </div>
              {saved.last_error && (
                <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                  Last error: {saved.last_error}
                </div>
              )}
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="text-sm font-semibold text-gray-800">Username</span>
                  <input
                    autoComplete="username"
                    value={form[key].username}
                    onChange={(event) => updateProvider(key, 'username', event.target.value)}
                    placeholder={saved.username_hint || 'Enter username'}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  {saved.username_hint && <span className="mt-1 block text-xs text-gray-500">Leave blank to keep {saved.username_hint}.</span>}
                </label>
                <label className="block">
                  <span className="text-sm font-semibold text-gray-800">Password</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={form[key].password}
                    onChange={(event) => updateProvider(key, 'password', event.target.value)}
                    placeholder={saved.has_password ? 'Saved — leave blank to keep' : 'Enter password'}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </label>
              </div>
              <label className="mt-4 flex items-start gap-3 rounded-md bg-gray-50 p-3">
                <input
                  type="checkbox"
                  checked={form[key].enabled}
                  onChange={(event) => updateProvider(key, 'enabled', event.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600"
                />
                <span className="text-sm text-gray-800">Enable automated searches through {label}</span>
              </label>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving || !settings?.encryption_ready}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save Title Search Settings'}
        </button>
        {anyConfigured && (
          <button
            type="button"
            onClick={clear}
            disabled={saving}
            className="rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
          >
            Remove Saved Credentials
          </button>
        )}
      </div>

      <div className="mt-7 border-t border-gray-200 pt-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2">
              <MagnifyingGlassIcon className="h-5 w-5 text-indigo-700" />
              <h3 className="text-lg font-semibold text-gray-900">All Filed Chapter 7 Cases</h3>
            </div>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              Run the debtor and alias searches for every Chapter 7 case whose status is Case Filed.
              Each case receives its own accepted Title Search Results document, and the entire batch
              is also combined into one master PDF.
            </p>
          </div>
          <button
            type="button"
            onClick={startBatch}
            disabled={
              batchLoading
              || batchStarting
              || !batchPreflight?.ready
              || ['queued', 'in_progress'].includes(batch?.status)
            }
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <MagnifyingGlassIcon className="h-4 w-4" />
            {batchStarting ? 'Starting…' : 'Run All Filed Chapter 7 Searches'}
          </button>
        </div>

        {batchError && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {batchError}
          </div>
        )}

        {batchLoading ? (
          <p className="mt-4 text-sm text-gray-500">Counting filed Chapter 7 cases and searches…</p>
        ) : batchPreflight && (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {[
                ['Filed Chapter 7 cases', batchPreflight.case_count || 0],
                ['Ready cases', batchPreflight.ready_case_count || 0],
                ['Cases needing information', batchPreflight.blocked_case_count || 0],
                ['Live searches', batchPreflight.total_searches || 0],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="text-2xl font-semibold text-gray-900">{value}</div>
                  <div className="mt-1 text-xs font-medium text-gray-600">{label}</div>
                </div>
              ))}
            </div>

            {(batchPreflight.blocked_case_count || 0) > 0 && (
              <details className="mt-4 rounded-lg border border-amber-200 bg-amber-50">
                <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-amber-900">
                  View cases that cannot be searched yet
                </summary>
                <div className="space-y-2 border-t border-amber-200 px-4 py-3">
                  {(batchPreflight.cases || []).filter((item) => !item.ready).map((item) => (
                    <div key={item.case_id} className="rounded bg-white px-3 py-2 text-sm">
                      <div className="font-semibold text-gray-900">
                        {item.case_number || item.case_id} · {item.debtor_names}
                      </div>
                      <div className="mt-1 text-xs text-red-700">{(item.blockers || []).join(' ')}</div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </>
        )}

        {batch && (
          <div className={`mt-5 rounded-lg border p-4 ${
            batch.status === 'succeeded'
              ? 'border-green-200 bg-green-50'
              : ['failed', 'partial'].includes(batch.status)
                ? 'border-amber-200 bg-amber-50'
                : 'border-blue-200 bg-blue-50'
          }`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-900">Latest bulk search: {batch.status_display}</div>
                <div className="mt-1 text-sm text-gray-700">{batch.status_message}</div>
                <div className="mt-1 text-xs text-gray-600">
                  {batch.completed_cases || 0} of {batch.total_cases || 0} cases processed ·{' '}
                  {batch.successful_cases || 0} completed · {batch.failed_cases || 0} need attention
                </div>
              </div>
              {batch.document && (
                <button
                  type="button"
                  onClick={downloadBatchPDF}
                  className="inline-flex items-center gap-2 rounded-md bg-green-700 px-4 py-2 text-sm font-semibold text-white hover:bg-green-800"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Download Master PDF
                </button>
              )}
            </div>
            {['queued', 'in_progress'].includes(batch.status) && (
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-blue-100">
                <div
                  className="h-full rounded-full bg-indigo-600 transition-all"
                  style={{
                    width: `${Math.max(
                      2,
                      Math.round(((batch.completed_cases || 0) / Math.max(1, batch.total_cases || 1)) * 100)
                    )}%`,
                  }}
                />
              </div>
            )}
            {batch.error_message && <div className="mt-3 text-sm text-red-800">{batch.error_message}</div>}
            {(batch.results || []).length > 0 && (
              <details className="mt-4 border-t border-gray-200 pt-3">
                <summary className="cursor-pointer text-sm font-semibold text-indigo-800">View case-by-case results</summary>
                <div className="mt-3 max-h-80 space-y-2 overflow-auto">
                  {batch.results.map((item) => (
                    <div key={item.case_id} className="rounded bg-white px-3 py-2 text-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-semibold text-gray-900">
                          {item.case_number || item.case_id} · {item.debtor_names || 'Unknown debtor'}
                        </span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          item.status === 'succeeded' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {item.status_display || item.status}
                        </span>
                      </div>
                      {item.error && <div className="mt-1 text-xs text-red-700">{item.error}</div>}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
